import React, { useState } from 'react';
import { Link, graphql } from 'gatsby';
import Slider from 'react-slick';

import { Swiper, SwiperSlide } from 'swiper6/react';
import SwiperCore, { Controller, Navigation } from 'swiper6';

import 'swiper6/swiper-bundle.min.css';
import 'swiper6/swiper.min.css';

import Layout from '../components/layout/Layout';
import LatestNews from '../components/news/LatestNews';
import PageTitle from '../components/common/PageTitle';
import LawSection from '../components/home/LawSection';

import LarsRasmussen from '../assets/images/learn/speakers/lars.png';
import DavidKerr from '../assets/images/learn/speakers/david.png';
import JasonBarnwell from '../assets/images/learn/speakers/jason.png';
import HelgaButcher from '../assets/images/learn/speakers/helga.png';
import StephanieHamon from '../assets/images/learn/speakers/stephanie.png';
import PaulHogg from '../assets/images/learn/speakers/paul.png';
import CatherineGoodman from '../assets/images/learn/speakers/catherine.png';
import GaryTully from '../assets/images/learn/speakers/gary.png';
import ChrisGrant from '../assets/images/learn/speakers/chris.png';

import ArrowDown from '../assets/images/learn/arrow-down.svg';
import ComingSoon from '../assets/images/learn/coming-soon.png';
import PlayIcon from '../assets/svg/play.svg';
import PlayWhite from '../assets/images/icons/play-white.svg';
import ArrowLeftIcon from '../assets/svg/arrow-left.svg';
import ArrowRightIcon from '../assets/svg/arrow-right.svg';

const speakers = [
  {
    name: 'Lars Rasmussen',
    description: 'Lars is a world renowned computer scientist, having co-founded Google Maps and having previously served as the director of engineering for Facebook. He holds a PhD in computer science from the University of California, Berkeley.',
    image: LarsRasmussen,
  },
  {
    name: 'David Kerr',
    description: 'David until recently was CEO of Bird & Bird for 26 years, and during that time built it up to be the leading global law firm advising companies being changed by tech. He has a passion for harnessing legaltech to improve the delivery of legal services, and advises or is involved with several companies and clients in the area.',
    image: DavidKerr,
  },
  {
    name: 'Jason Barnwell',
    description: 'Jason is an attorney in Microsoft’s Corporate, External and Legal Affairs department. He leads the Microsoft’s Modern Legal team, with previous roles leading Legal Business, Operations and Strategy team, leading the Open Source Software practice group, and counseling the Cloud and AI platform business.',
    image: JasonBarnwell,
  },
  {
    name: 'Paul Hogg',
    description: 'Paul is a Relationship Manager at Barclays. He works within a dynamic team who are tasked with driving change across the legal function and delivering at pace. He plays a pivotal role in panel firm relationships.',
    image: PaulHogg,
  },
  {
    name: 'Stéphanie Hamon',
    description: 'Stéphanie ensures that the legal function of major corporations operates as an efficient business partner. She seeks to transform legal service delivery one step at a time. Expert in setting and delivering commercial management strategies for in-house legal departments as well as broader legal operations strategies and implementation.',
    image: StephanieHamon,
  },
  {
    name: 'Chris Grant',
    description: 'Chris has broad experience working across the legal industry with time spent in both private practice and in-house. The majority of his career has been focused on legal operations, bringing efficiencies to traditional ways of working.',
    image: ChrisGrant,
  },
  {
    name: 'Helga Butcher',
    description: 'Helga is the Interim Head of Legal Project Management and Process Improvement (EMEA) at Ashurst. She has a wealth of experience in legal project management (LPM) and has recently collaborated with industry pioneers in the creation of a LPM competency framework to help professionalise the role of an LPM.',
    image: HelgaButcher,
  },
  {
    name: 'Catherine Goodman',
    description: 'Cathy leads the Practice Innovation & Knowledge Counsel at Paul Hastings, and is part of the global team named 2020 ‘Innovators of the Year’ by the International Legal Technology Association for outstanding achievements in maximising the value of innovative technology in support of legal professionals.',
    image: CatherineGoodman,
  },
  {
    name: 'Gary Tully',
    description: 'Gary is the Head of Legal Operations at Gilead. He has a demonstrated history of working in the biotechnology and high tech industries. In late 2021, Gary’s team was awarded law.com’s ‘Best Legal Ops Team’ having navigated the development of Covid-19 treatment amidst the pandemic.',
    image: GaryTully,
  },
];

SwiperCore.use([Controller, Navigation]);

function Learn({ data }) {
  const { allMarkdownRemark } = data;
  const { nodes } = allMarkdownRemark;
  const episodes = nodes.map((n) => ({
    episode: n.frontmatter.episode,
    slug: n.frontmatter.slug,
    title: n.frontmatter.title,
    subheading: n.frontmatter.subheading,
    subtitle: n.frontmatter.subtitle,
    duration: n.frontmatter.duration,
    thumbnail: n.frontmatter.thumbnail,
    speaker: n.frontmatter.speaker,
  })).sort((a, b) => ((a.episode > b.episode) ? 1 : -1));

  const [showMore, setShowMore] = useState(false);
  const [firstSwiper, setFirstSwiper] = useState(null);
  const [secondSwiper, setSecondSwiper] = useState(null);

  const comingSoonEpisodes = [];

  for (let i = 12; i <= 11; i += 1) {
    comingSoonEpisodes.push(i);
  }

  const batchMainEpisodes = [
    {
      title: 'Learn from the shoulders of Giants',
      caption: 'We reveal the secrets from the battlefield in a series of masterclasses conducted by some of the most influential thinkers in the technology and legal services fields.',
      episode: episodes[0],
    },
    {
      title: 'Legal Operations in Practice',
      caption: 'Hear from our experts and clients as they share their experiences from the battlefield about innovation initiatives, the burgeoning role of legal project managers, and why legal operations is here to stay.',
      episode: episodes[3],
    },
  ];

  return (
    <Layout
      title="Learn"
      image="/metaimages/masterclass.png"
      description="Learn from the shoulders of Giants. We reveal the secrets from the battlefield in a series of masterclasses conducted by some of the most influential thinkers in the technology and legal services fields."
    >
      <div className="d-none d-lg-block">
        <PageTitle title="Learn" />
      </div>
      <div className="learn-banner">
        <div className="brief-container">
          <div className="half-fit-width">
            <div className="brief">
              <Swiper
                onSwiper={setFirstSwiper}
                controller={{ control: secondSwiper }}
                navigation={
                  {
                    nextEl: '#banner-swiper-next',
                    prevEl: '#banner-swiper-prev',
                  }
                }
                loop
                observer
                observeParents
              >
                {
                  batchMainEpisodes.map((e) => (
                    <SwiperSlide key={e.episode.episode}>
                      <div>
                        <div className="top">
                          {e.title}
                        </div>
                        <p className="bottom">
                          {e.caption}
                        </p>
                      </div>
                    </SwiperSlide>
                  ))
                }
              </Swiper>
              <div className="button-container">
                <button id="banner-swiper-prev">
                  <ArrowLeftIcon fill="black" />
                </button>
                <button id="banner-swiper-next">
                  <ArrowRightIcon fill="black" />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="banner-image-swiper">
          <Swiper
            onSwiper={setSecondSwiper}
            controller={{ control: firstSwiper }}
            loop
            observer
            observeParents
          >
            {
                batchMainEpisodes.map((e) => (
                  <SwiperSlide key={e.episode.episode}>
                    <div className="watch-episode-container">
                      <div className="image" style={{ backgroundImage: `url(${e.episode.thumbnail})` }} />
                      <div className="episode">
                        <Link to={e.episode.slug}>
                          <button>
                            <PlayIcon />
                            <span className="ml-2">Start watching</span>
                          </button>
                        </Link>
                        <div className="details">
                          <div className="number">
                            Episode
                            {' '}
                            {e.episode.episode}
                          </div>
                          <div className="title">
                            {`${e.episode.title} - ${e.episode.subheading}`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))
              }
          </Swiper>
        </div>
      </div>
      <div className="container-fluid px-0 pt-100 pb-100 pt-sm-80 pb-sm-80 bg-white">
        <div className="episode-list fit-width">
          <div className="header">Episodes</div>
          <div className="list">
            {
              episodes.slice(0, 11).map((e) => (
                <Link to={e.slug} key={e.episode} className="episode">
                  <div className="preview">
                    <img src={e.thumbnail} alt="preview" />
                    <button
                      className="play-button center"
                    >
                      <img src={PlayWhite} className="icon" alt="play icon" />
                    </button>
                  </div>
                  <div className="details">
                    <div className="count">
                      {`Episode ${e.episode}`}
                    </div>
                    <div className="title">{`${e.title} ${e.subheading ? `- ${e.subheading}` : ''}`}</div>
                    <div className="speaker-credentials">
                      {
                        e.speaker
                          ? `by ${e.speaker}`
                          : ''
                      }
                    </div>
                    <div className="content">{e.subtitle}</div>
                    <div className="duration">{e.duration}</div>
                  </div>
                </Link>
              ))
            }
            {
              false
                ? (
                  <>
                    {
                      comingSoonEpisodes.map((episode) => (
                        <div className="episode" key={episode}>
                          <div className="preview">
                            <img src={ComingSoon} alt="coming soon" />
                          </div>
                          <div className="details">
                            <div className="count">
                              {`Episode ${episode}`}
                            </div>
                            <div className="title">Coming Soon</div>
                          </div>
                        </div>
                      ))
                    }
                  </>
                ) : ''
            }
          </div>
          {
            false
              && (
                <div className="show">
                  <button className="button" onClick={() => setShowMore(!showMore)}>
                    <span className="label mr-3 tracking-[-0.02em]">
                      Show
                      {showMore ? ' less' : ' more'}
                    </span>
                    <img
                      className={showMore ? 'invert' : 'arrow'}
                      width="16"
                      height="10"
                      alt="arrow down"
                      src={ArrowDown}
                    />
                  </button>
                </div>
              )
          }
        </div>
      </div>
      <LawSection />
      <div className="container-fluid px-0 pt-100 pb-100 pt-sm-80 pb-sm-80 bg-white d-none d-md-block">
        <div className="speaker-list fit-width">
          <div className="header">Speakers</div>
          <div className="list">
            {speakers.map((speaker) => (
              <div className="speaker" key={speaker.name}>
                <img className="image" src={speaker.image} alt="speaker" />
                <div className="details">
                  <div className="name">{speaker.name}</div>
                  <div className="brief">{speaker.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="container-fluid mx-0 pt-100 pt-sm-80 bg-white d-md-none">
        <div className="speaker-list">
          <div className="header mb-0">Speakers</div>
        </div>
      </div>
      <div className="speaker-list pt-32 pb-100 pb-sm-80 bg-white d-md-none" style={{ width: '100vw' }}>
        <Slider centerMode arrows={false}>
          {speakers.map((speaker) => (
            <div key={speaker.name} className="px-4">
              <div className="speaker">
                <img className="image" src={speaker.image} alt="speaker" />
                <div className="details">
                  <div className="name">{speaker.name}</div>
                  <div className="brief">{speaker.description}</div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </div>
      <LatestNews className="job-post-section fit-width" />
    </Layout>
  );
}

export const pageQuery = graphql`
  query {
    allMarkdownRemark {
      nodes {
        frontmatter {
          duration
          episode
          title
          subheading
          subtitle
          slug
          thumbnail
          speaker
        }
      }
    }
  }
`;

export default Learn;
